import {useContext, useEffect, useState} from "react";
import {Button, Form, Modal, Spinner} from "react-bootstrap";
import {useForm} from "react-hook-form";
import * as yup from "yup";
import {toast} from "react-toastify";
import {getApiErrorMessage, useYupValidationResolver} from "../../common";
import {ErrorMessage} from "../../parts/error-message";
import {
    HealthConditionMaster,
    createHealthConditionMaster,
    deleteHealthConditionMaster,
    updateHealthConditionMaster,
} from "../../api-service/health-condition-master";
import {DeleteConfirmModal} from "../department-job-mapping/delete-confirm-modal";
import {TsAdminContext} from "../../context/ts-admin";

const trimString = (value: unknown) => (typeof value === "string" ? value.trim() : value);

const MAX_LENGTH_CODE = 6;
const MAX_LENGTH_NAME = 20;

const validationSchema = yup.object({
    Code: yup.string()
        .transform(trimString)
        .required("コードを入力してください")
        .max(MAX_LENGTH_CODE, `コードは${MAX_LENGTH_CODE}文字以内で入力してください`)
        // The device sends the selected codes joined by ',' — keep in sync with api-health-condition-master-func
        .matches(/^[^,，]*$/, "コードに「,」は使用できません"),
    Name: yup.string()
        .transform(trimString)
        .required("名称を入力してください")
        .max(MAX_LENGTH_NAME, `名称は${MAX_LENGTH_NAME}文字以内で入力してください`),
});

const emptyFormValues = {
    Code: "",
    Name: "",
};

interface HealthConditionFormModalProps {
    show: boolean;
    mode: "create" | "edit";
    initialData: HealthConditionMaster | null;
    onClose: () => void;
    onSaved: () => void;
    onDeleted: () => void;
}

export function HealthConditionFormModal({show, mode, initialData, onClose, onSaved, onDeleted}: HealthConditionFormModalProps) {
    const [orgInfo] = useContext(TsAdminContext);
    const resolver = useYupValidationResolver(validationSchema);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: {errors, isSubmitted},
    } = useForm({
        resolver,
        defaultValues: emptyFormValues,
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    useEffect(() => {
        if (!show) return;
        if (mode === "edit" && initialData) {
            reset({
                Code: initialData.Code,
                Name: initialData.Name || "",
            });
        } else {
            reset(emptyFormValues);
        }
    }, [show, mode, initialData, reset]);

    const submitFormData = async (data: typeof emptyFormValues) => {
        setIsSaving(true);
        const targetOrg = localStorage.getItem('target-org') || orgInfo?.currentOrg?.OrgId;
        const payload = {
            Code: data.Code,
            Name: data.Name,
        };
        try {
            if (mode === "edit" && initialData) {
                await updateHealthConditionMaster(initialData.Code, payload, targetOrg);
                toast.success("更新しました。");
            } else {
                await createHealthConditionMaster(payload, targetOrg);
                toast.success("登録しました。");
            }
            onSaved();
        } catch (error: any) {
            const fallback = mode === "edit" ? "データ編集に失敗しました。再度お試しください。" : "データ登録に失敗しました。再度お試しください。";
            toast.error(getApiErrorMessage(error, fallback));
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!initialData) return;
        const targetOrg = localStorage.getItem('target-org') || orgInfo?.currentOrg?.OrgId;
        try {
            await deleteHealthConditionMaster(initialData.Code, targetOrg);
            toast.success("削除しました。");
            setShowDeleteConfirm(false);
            onDeleted();
        } catch (error: any) {
            toast.error(getApiErrorMessage(error, "データ削除に失敗しました。再度お試しください。"));
        }
    };

    return (
        <Modal className="health-condition-modal pt-5" show={show} onHide={onClose}>
            <Form onSubmit={handleSubmit(submitFormData)}>
                <Modal.Header closeButton>
                    {mode === "edit" ? "編集" : "新規作成"}
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label fs-12">コード</Form.Label>
                        <div className="d-flex w-100">
                            <Form.Control className="modal-form-control" size="sm" maxLength={MAX_LENGTH_CODE} {...register("Code")}/>
                        </div>
                    </div>
                    <div className="d-flex">
                        <Form.Label className="modal-form-label fs-12"></Form.Label>
                        <div className="d-flex w-100 ms-2">
                            {isSubmitted && <ErrorMessage name="Code" className="px-0 pt-1 mb-0" errors={errors}/>}
                        </div>
                    </div>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label fs-12">名称</Form.Label>
                        <div className="d-flex w-100">
                            <Form.Control className="modal-form-control" size="sm" maxLength={MAX_LENGTH_NAME} {...register("Name")}/>
                        </div>
                    </div>
                    <div className="d-flex">
                        <Form.Label className="modal-form-label fs-12"></Form.Label>
                        <div className="d-flex w-100 ms-2">
                            {isSubmitted && <ErrorMessage name="Name" className="px-0 pt-1 mb-0" errors={errors}/>}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="modal-footer">
                    {mode === "edit" && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            className="bg-white modal-btn me-auto btn-delete-mapping"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            削除
                        </Button>
                    )}
                    <Button variant="light" size="sm" className="modal-btn modal-cancel-btn" onClick={onClose}>
                        キャンセル
                    </Button>
                    <Button variant="primary" style={{background: "#0176D3"}} size="sm" type="submit"
                            className="modal-btn btn-save-common" disabled={isSaving}>
                        {isSaving ? (
                            <>
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true"/>
                                保存...
                            </>
                        ) : (
                            "保存"
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
            <DeleteConfirmModal
                show={showDeleteConfirm}
                onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                message="この登録されているデータを削除します。"
            />
        </Modal>
    );
}
